import { useState, useEffect, useCallback } from 'react';
import { posAPI, contractsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

/*
 * Offline queue persistence.
 * We use **localStorage** (not sessionStorage) so queued transactions survive
 * a browser/tab close — the original sessionStorage implementation caused
 * data loss when iPad Safari evicted the tab or the user closed the app
 * before reconnecting to wifi (incident: Feb 25, 2026).
 */
const QUEUE_KEY = 'pos_offline_queue_v1';
const LEGACY_SESSION_KEY = 'pos_offline_queue_v1'; // same name, used to be sessionStorage
const readQueue = () => {
  // Read primary (localStorage); fall back to any stale sessionStorage data
  // so a user who upgrades mid-shift doesn't silently lose what's queued.
  try {
    const fromLocal = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    if (fromLocal.length > 0) return fromLocal;
  } catch { /* corrupt JSON — fall through */ }
  try {
    const fromSession = JSON.parse(sessionStorage.getItem(LEGACY_SESSION_KEY) || '[]');
    if (fromSession.length > 0) {
      // Migrate forward immediately
      try { localStorage.setItem(QUEUE_KEY, JSON.stringify(fromSession)); } catch { /* quota */ }
      return fromSession;
    }
  } catch { /* ignore */ }
  return [];
};
const writeQueue = (q) => {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch { /* quota / disabled — best effort */ }
};
const uuidish = () => (
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
);

/*
 * Normalise an error from the backend into a string we can safely render
 * inside a toast description.  FastAPI's 422 detail is an array of
 * { type, loc, msg, input, url } objects which React refuses to render.
 */
const stringifyApiError = (err, fallback = 'Something went wrong') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => {
      if (typeof d === 'string') return d;
      const loc = Array.isArray(d?.loc) ? d.loc.filter(p => p !== 'body').join('.') : '';
      return loc ? `${loc}: ${d?.msg || 'invalid'}` : (d?.msg || 'invalid');
    }).join('; ');
  }
  try { return JSON.stringify(detail); } catch { return fallback; }
};

export const usePOS = () => {
  const { toast } = useToast();
  const [view, setView] = useState('sessions');
  const [sessions, setSessions] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [drinkMenu, setDrinkMenu] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [cart, setCart] = useState([]);
  const [tip, setTip] = useState('');
  const [amountReceived, setAmountReceived] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewSession, setShowNewSession] = useState(false);
  const [selectedContract, setSelectedContract] = useState('');
  const [startingInventory, setStartingInventory] = useState({});
  const [endingInventory, setEndingInventory] = useState({});
  const [sessionReport, setSessionReport] = useState(null);
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [restockAmounts, setRestockAmounts] = useState({});
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  /* ────────── Offline support ────────── */
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingQueue, setPendingQueue] = useState(readQueue);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadInitialData();
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('resize', handleResize);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const enqueue = (tx) => {
    setPendingQueue(prev => {
      const next = [...prev, tx];
      writeQueue(next);
      return next;
    });
  };

  const removeFromQueue = (clientId) => {
    setPendingQueue(prev => {
      const next = prev.filter(t => t.client_transaction_id !== clientId);
      writeQueue(next);
      return next;
    });
  };

  /* Drain the offline queue in order. Idempotent — backend dedupes on client_transaction_id. */
  const syncPendingQueue = useCallback(async () => {
    if (syncing) return;
    const queue = readQueue();
    if (queue.length === 0) return;
    setSyncing(true);
    let successCount = 0;
    let failed = null;
    for (const tx of queue) {
      try {
        await posAPI.createTransaction(tx.payload);
        removeFromQueue(tx.client_transaction_id);
        successCount += 1;
      } catch (err) {
        // Stop on first hard failure (likely still offline or session-closed).
        // Idempotency means partial replay is safe to retry later.
        failed = err;
        break;
      }
    }
    setSyncing(false);
    if (successCount > 0) {
      toast({ title: `Synced ${successCount} offline transaction(s)` });
      // Refresh transactions + session totals
      if (activeSession?.id) {
        try {
          const [txRes, sessionRes] = await Promise.all([
            posAPI.getSessionTransactions(activeSession.id),
            posAPI.getSession(activeSession.id),
          ]);
          setTransactions(txRes.data || []);
          setActiveSession(sessionRes.data);
        } catch { /* network blip */ }
      }
    }
    if (failed) {
      toast({
        title: 'Sync paused',
        description: stringifyApiError(failed, 'Will resume on next reconnect'),
        variant: 'destructive',
      });
    }
  }, [syncing, activeSession, toast]);

  /* Auto-sync when we come back online */
  useEffect(() => {
    if (isOnline) syncPendingQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  /*
   * Belt-and-braces: periodically retry the queue while online.
   * iOS Safari sometimes suspends tabs and the `online` event never fires
   * on resume, so a manual poll every 30 s prevents data sitting stuck
   * in localStorage forever. No-op when the queue is empty.
   */
  useEffect(() => {
    const interval = setInterval(() => {
      if (navigator.onLine && readQueue().length > 0) {
        syncPendingQueue();
      }
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInitialData = async () => {
    try {
      const [sessionsRes, contractsData, menuRes] = await Promise.all([
        posAPI.getAllSessions(),
        contractsAPI.getAll(),
        posAPI.getDrinkMenu()
      ]);
      setSessions(sessionsRes.data || []);
      setContracts(contractsData || []);
      setDrinkMenu(menuRes.data || []);
    } catch (err) {
      console.error('POS load error:', err);
      toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleStartSession = async () => {
    if (!selectedContract) {
      toast({ title: 'Error', description: 'Please select an event', variant: 'destructive' });
      return;
    }
    try {
      const res = await posAPI.createSession({
        contract_id: selectedContract, starting_inventory: startingInventory, drink_prices: {}
      });
      setActiveSession(res.data);
      setShowNewSession(false);
      setView('pos');
      toast({ title: 'Success', description: 'POS session started' });
      loadInitialData();
    } catch (err) {
      toast({ title: 'Error', description: stringifyApiError(err, 'Failed to start session'), variant: 'destructive' });
    }
  };

  const handleCloseSession = async () => {
    if (!activeSession) return;
    // Guard: don't allow closing while offline transactions are still queued
    if (pendingQueue.length > 0) {
      toast({
        title: 'Cannot close session yet',
        description: `${pendingQueue.length} offline transaction(s) still need to sync. ${isOnline ? 'Click "Sync now" or wait a moment.' : 'Reconnect to the network first.'}`,
        variant: 'destructive',
      });
      return;
    }
    if (!isOnline) {
      toast({
        title: 'You are offline',
        description: 'Reconnect to the network before closing the session.',
        variant: 'destructive',
      });
      return;
    }
    try {
      await posAPI.closeSession(activeSession.id, { ending_inventory: endingInventory });
      const reportRes = await posAPI.getSessionReport(activeSession.id);
      setSessionReport(reportRes.data);
      setView('report');
      toast({ title: 'Success', description: 'Session closed' });
      loadInitialData();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to close session', variant: 'destructive' });
    }
  };

  const handleResumeSession = async (session) => {
    setActiveSession(session);
    const txRes = await posAPI.getSessionTransactions(session.id);
    setTransactions(txRes.data || []);
    setView('pos');
  };

  const handleViewReport = async (session) => {
    const reportRes = await posAPI.getSessionReport(session.id);
    setSessionReport(reportRes.data);
    setActiveSession(session);
    setView('report');
  };

  const handleRestock = async () => {
    const updates = Object.fromEntries(
      Object.entries(restockAmounts).filter(([_, qty]) => qty > 0)
    );
    if (Object.keys(updates).length === 0) {
      toast({ title: 'Error', description: 'Please enter quantities to restock', variant: 'destructive' });
      return;
    }
    try {
      const res = await posAPI.restockInventory(activeSession.id, { inventory_updates: updates });
      setActiveSession(res.data);
      setRestockAmounts({});
      setShowRestockModal(false);
      toast({ title: 'Success', description: 'Inventory restocked!' });
    } catch (err) {
      toast({ title: 'Error', description: stringifyApiError(err, 'Failed to restock'), variant: 'destructive' });
    }
  };

  const addToCart = (drink) => {
    const existing = cart.find(item => item.drink_id === drink.id);
    if (existing) {
      setCart(cart.map(item => item.drink_id === drink.id ? { ...item, quantity: item.quantity + 1 } : item));
    } else {
      setCart([...cart, { drink_id: drink.id, name: drink.name, price: drink.default_price, quantity: 1 }]);
    }
  };

  const updateCartQuantity = (drinkId, delta) => {
    setCart(cart.map(item => {
      if (item.drink_id === drinkId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : null;
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (drinkId) => setCart(cart.filter(item => item.drink_id !== drinkId));

  // Prices in `cart` are stored TAX-INCLUSIVE (what the customer pays).
  // The "subtotal" line on the receipt should show pre-tax amounts, then HST, then the total.
  const POS_TAX_RATE = 0.13;
  const cartTotalPostTax = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartSubtotalPreTax = +(cartTotalPostTax / (1 + POS_TAX_RATE)).toFixed(2);
  const cartTaxAmount = +(cartTotalPostTax - cartSubtotalPreTax).toFixed(2);
  const cartTotal = cartTotalPostTax;  // keep legacy alias used elsewhere
  // Tip = amount received minus total. Negative tip → silently $0.
  const amountReceivedNum = parseFloat(amountReceived) || 0;
  const tipFromCash = Math.max(0, +(amountReceivedNum - cartTotalPostTax).toFixed(2));
  // For backward compat with the rest of the hook, the legacy `tip` state mirrors tipFromCash
  const grandTotal = +(cartTotalPostTax + tipFromCash).toFixed(2);

  const handleProcessSale = async () => {
    if (cart.length === 0) {
      toast({ title: 'Error', description: 'Cart is empty', variant: 'destructive' });
      return;
    }
    const clientTxId = uuidish();
    // Derived tip (amount received minus total). Falls back to 0 if AR < total.
    const tipValue = tipFromCash;
    const payload = {
      session_id: activeSession.id,
      items: cart.map(item => ({ drink_id: item.drink_id, quantity: item.quantity })),
      tip: tipValue,
      amount_received: amountReceivedNum,
      tax_rate: POS_TAX_RATE,
      payment_method: paymentMethod,
      client_transaction_id: clientTxId,
      client_timestamp: new Date().toISOString(),
    };

    // Compute totals locally so optimistic UI works offline
    const subtotalPostTax = cart.reduce((s, it) => s + it.price * it.quantity, 0);
    const subtotalPreTax = +(subtotalPostTax / (1 + POS_TAX_RATE)).toFixed(2);
    const taxAmount = +(subtotalPostTax - subtotalPreTax).toFixed(2);
    const total = +(subtotalPostTax + tipValue).toFixed(2);
    const optimisticTx = {
      id: `pending-${clientTxId}`,
      client_transaction_id: clientTxId,
      session_id: activeSession.id,
      contract_id: activeSession.contract_id,
      items: cart.map(it => ({
        drink_id: it.drink_id, name: it.name, quantity: it.quantity, price: it.price, subtotal: it.price * it.quantity,
      })),
      subtotal: subtotalPostTax,
      subtotal_pre_tax: subtotalPreTax,
      tax_amount: taxAmount,
      tax_rate: POS_TAX_RATE,
      tip: tipValue,
      amount_received: amountReceivedNum,
      total,
      payment_method: paymentMethod,
      timestamp: payload.client_timestamp,
      _pending: true,  // marker for UI
    };

    // Always reset cart immediately for snappy UX
    setCart([]);
    setTip('');
    setAmountReceived('');

    if (!isOnline) {
      enqueue({ client_transaction_id: clientTxId, payload });
      setTransactions(prev => [optimisticTx, ...prev]);
      // Optimistically update session total_sales so the on-screen running total stays accurate
      setActiveSession(prev => prev ? {
        ...prev,
        total_sales: (prev.total_sales || 0) + subtotalPostTax,
        total_tips: (prev.total_tips || 0) + tipValue,
      } : prev);
      toast({ title: 'Saved offline', description: 'Will sync automatically when reconnected.' });
      return;
    }

    try {
      const res = await posAPI.createTransaction(payload);
      setTransactions(prev => [res.data, ...prev]);
      const sessionRes = await posAPI.getSession(activeSession.id);
      setActiveSession(sessionRes.data);
      toast({ title: 'Success', description: 'Sale processed!' });
    } catch (err) {
      // Network failure mid-sale → queue and treat as offline
      enqueue({ client_transaction_id: clientTxId, payload });
      setTransactions(prev => [optimisticTx, ...prev]);
      setActiveSession(prev => prev ? {
        ...prev,
        total_sales: (prev.total_sales || 0) + subtotalPostTax,
        total_tips: (prev.total_tips || 0) + tipValue,
      } : prev);
      toast({ title: 'Network issue', description: 'Saved offline; will retry on reconnect.', variant: 'destructive' });
      // If we're nominally still online (transient 500), schedule a retry shortly
      if (isOnline) setTimeout(() => syncPendingQueue(), 3000);
    }
  };

  const handleVoidTransaction = async (txId) => {
    try {
      await posAPI.voidTransaction(txId);
      setTransactions(transactions.filter(tx => tx.id !== txId));
      const sessionRes = await posAPI.getSession(activeSession.id);
      setActiveSession(sessionRes.data);
      toast({ title: 'Success', description: 'Transaction voided' });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to void transaction', variant: 'destructive' });
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'beer': return 'bg-amber-500 hover:bg-amber-600';
      case 'wine': return 'bg-red-700 hover:bg-red-800';
      case 'spirits': return 'bg-blue-600 hover:bg-blue-700';
      case 'cocktails': return 'bg-purple-600 hover:bg-purple-700';
      case 'non_alcoholic': return 'bg-green-600 hover:bg-green-700';
      default: return 'bg-gray-600 hover:bg-gray-700';
    }
  };

  return {
    view, setView, sessions, contracts, drinkMenu, activeSession, setActiveSession,
    cart, tip, setTip, paymentMethod, setPaymentMethod, transactions,
    amountReceived, setAmountReceived, cartSubtotalPreTax, cartTaxAmount, cartTotalPostTax,
    tipFromCash, POS_TAX_RATE,
    loading, showNewSession, setShowNewSession, selectedContract, setSelectedContract,
    startingInventory, setStartingInventory, endingInventory, setEndingInventory,
    sessionReport, setSessionReport,
    showRestockModal, setShowRestockModal, restockAmounts, setRestockAmounts,
    isMobileView,
    cartTotal, grandTotal,
    handleStartSession, handleCloseSession, handleResumeSession, handleViewReport,
    handleRestock, addToCart, updateCartQuantity, removeFromCart,
    handleProcessSale, handleVoidTransaction, getCategoryColor,
    // Offline support
    isOnline, pendingQueue, syncing, syncPendingQueue
  };
};
